import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { SettingsService, SiteIds } from '../../core/settings.service';
import { IRadioGroupItem } from '../../form-elements/radio-button/radio-button.component';
import {
    IMinMaxRanges,
    ITickmark,
} from '../../form-elements/range/range.component';
import { IToggleButtonsGroupItem } from '../../form-elements/toggle-buttons-group/toggle-buttons-group';
import {
    IActiveLeasingView,
    LEASING_PLAN_TYPES,
    LeasingService,
} from '../leasing.service';

export type ILeasingConfiguration = {
    downPayment: number;
    monthlyTerms: number;
    yearlyDistanceInKm: number;
    residualValue?: number;
    leasingPlanType?: string;
    leasingPurpose?: string;
};

export interface ILeasingConfiguratorRanges {
    downPaymentRange: IMinMaxRanges;
    leasingMonthsRange: ITickmark[];
    yearlyDistanceInKmRange: IMinMaxRanges;
    residualValueRange: IMinMaxRanges;
}

@Component({
    selector: 'app-leasing-configurator',
    template: `
        <div class="leasing-configurator" [ngClass]="{'alt-theme': this.altTheme}">
            <form *ngIf="form && dataReady" class="leasing-form" [formGroup]="form" (ngSubmit)="onSubmit()">
                <!-- Leasing toggles -->
                <div class="leasing-configurator__input-section" *ngIf="leasingTypes && leasingTypes.length > 1">
                    <h6 class="text-small text-uppercase">{{ 'leasing.configurator____leasingtype' | translate }}</h6>
                    <app-input-toggle-group
                        [parentForm]="form"
                        controlName="leasingPlanType"
                        [group]="leasingTypes"
                        (handleValueChange)="selectLeasingPlan($event)">
                    </app-input-toggle-group>
                </div>
                <div class="leasing-configurator__input-section" *ngIf="leasingPurposes && leasingPurposes.length > 1">
                    <h6 class="text-small text-uppercase">{{ 'leasing.configurator____purpose' | translate }}</h6>
                    <app-input-toggle-group
                        [parentForm]="form"
                        controlName="leasingPurpose"
                        [group]="leasingPurposes"
                        (handleValueChange)="selectLeasingPurpose($event)">
                    </app-input-toggle-group>
                </div>
                <!-- Months toggle buttons -->
                <div class="leasing-configurator__input-section">
                    <app-toggle-buttons-group
                        [parentForm]="form"
                        controlName="monthlyTerms"
                        [group]="montshButtonsRange"
                        [active]="currentMontshButtonsValue"
                        [textSuffix]="'mdr.'"
                        (handleValueChange)="handleMonthTermChange($event)"
                    ></app-toggle-buttons-group>
                </div>
                <!-- First payment slider -->
                <div class="leasing-configurator__input-section" *ngIf="downPaymentRange">
                    <app-input-range
                        class="user-info-input"
                        cssClasses="input__input-element--white-background"
                        controlName="downPayment"
                        [parentForm]="form"
                        [label]="leasingTexts?.leasingPlanStep?.firstTimePaymentText"
                        [labelUnit]="currentDownPaymentTermsLabel | defaultcurrency"
                        (handleValueChange)="handleFirstPaymentTermChange($event)"
                        (liveOnChangeValueUseWithCautionTriggersOnEverySingleValueChange)="handleFirstPaymentTermLabelChange($event)"
                        [inputStepSize]="1000"
                        [minVal]="downPaymentRange.minValue"
                        [maxVal]="downPaymentRange.maxValue"
                        [minValLabel]="downPaymentRange.minValue | defaultcurrency"
                        [maxValLabel]="downPaymentRange.maxValue | defaultcurrency"
                        [isDisabled]="isDisabled">
                    </app-input-range>
                </div>
                <!-- Kilometers per year slider -->
                <div class="leasing-configurator__input-section" *ngIf="yearlyDistanceInKmRange">
                    <app-input-range
                        class="user-info-input"
                        cssClasses="input__input-element--white-background"
                        controlName="yearlyDistanceInKm"
                        [parentForm]="form"
                        [label]="leasingTexts?.general?.kmPerYearText"
                        [labelUnit]="(currentMileageTermsLabel | number) + ' km'"
                        (handleValueChange)="handleMileageChange($event)"
                        (liveOnChangeValueUseWithCautionTriggersOnEverySingleValueChange)="handleMileageLabelChange($event)"
                        [inputStepSize]="5000"
                        [minVal]="yearlyDistanceInKmRange.minValue"
                        [maxVal]="yearlyDistanceInKmRange.maxValue"
                        [minValLabel]="(yearlyDistanceInKmRange.minValue | number: '1.0-0') + ' km'"
                        [maxValLabel]="(yearlyDistanceInKmRange.maxValue | number: '1.0-0') + ' km'"
                        [isDisabled]="isDisabled">
                    </app-input-range>
                </div>

                <!-- Residual value slider -->
                <!-- <div class="leasing-configurator__input-section" *ngIf="showResidualValue">
                    <app-input-range
                        class="user-info-input"
                        cssClasses="input__input-element--white-background"
                        controlName="residualValue"
                        [parentForm]="form"
                        [label]="leasingTexts?.general?.residualValueText | uppercase"
                        [labelUnit]="currentResidualValueLabel | defaultcurrency"
                        (handleValueChange)="handleResidualValueChange($event)"
                        (liveOnChangeValueUseWithCautionTriggersOnEverySingleValueChange)="handleResidualValueLabelChange($event)"
                        [inputStepSize]="1000"
                        [minVal]="residualValueRange.minValue"
                        [maxVal]="residualValueRange.maxValue"
                        [minValLabel]="residualValueRange.minValue | defaultcurrency"
                        [maxValLabel]="residualValueRange.maxValue | defaultcurrency">
                    </app-input-range>
                </div> -->
            </form>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LeasingConfiguratorComponent implements OnInit, OnDestroy, OnChanges {
    @Input() leasingTexts: any;
    @Input() showResidualValue?: boolean = true;
    @Input() isDisabled?: boolean = false;
    @Input() altTheme = false;

    // Ranges
    @Input() leasingMonthsRange?: ITickmark[];
    @Input() downPaymentRange: IMinMaxRanges;
    @Input() yearlyDistanceInKmRange: IMinMaxRanges;
    @Input() residualValueRange?: IMinMaxRanges;

    @Input() leasingTypes?: IRadioGroupItem[] | null ;
    @Input() leasingPurposes?: IRadioGroupItem[] | null;

    // Values
    @Input() downPayment?: number;
    @Input() monthlyTerms?: number;
    @Input() yearlyDistanceInKm?: number;
    @Input() residualValue?: number;

    // Emitter
    @Output() handleChange = new EventEmitter<ILeasingConfiguration>();

    // Placeholder variables to hold values while they change before submit
    currentDownPaymentTerms?: ITickmark;
    currentDownPaymentTermsLabel?: number;

    currentLeasingMonthTerms?: ITickmark;
    currentLeasingMonthTermsLabel?: number;

    currentMileageTerms?: ITickmark;
    currentMileageTermsLabel?: number;

    currentResidualValue?: ITickmark;
    currentResidualValueLabel?: number;

    currentLeasingTypeValue?: string;
    currentLeasingTypeLabel?: string;

    currentLeasingPurposeValue?: string;
    currentLeasingPurposeLabel?: string;

    montshButtonsRange: IToggleButtonsGroupItem[];
    currentMontshButtonsValue: IToggleButtonsGroupItem;

    form?: UntypedFormGroup;
    previousFormData: any = {};

    site: string;
    siteIds = SiteIds;

    leasingType: string | undefined;
    leasingPurpose: string | undefined;

    dataReady: boolean;

    private readonly unsubscribe = new Subject<void>();

    constructor(
        private cdr: ChangeDetectorRef,
        private fb: UntypedFormBuilder,
        private leasingService: LeasingService,
        private settingsService: SettingsService
    ) {
        this.settingsService.getSiteId().pipe(takeUntil(this.unsubscribe)).subscribe((siteId: string) => {
            this.site = siteId;
        });
    }

    ngOnInit() {
        // Default values
        if (this.downPayment && this.downPayment > this.downPaymentRange.maxValue) {
            this.downPayment = this.downPaymentRange.maxValue;
        }

        // Check if any existing values have been selected previously
        this.leasingService.getActiveLeasingToggles().pipe(take(1)).subscribe((activeLeasingView: IActiveLeasingView | undefined) => {
            if (activeLeasingView) {
                this.leasingType = activeLeasingView.leasingType;
                this.leasingPurpose = activeLeasingView.leasingPurpose;
            } else {
                if (this.site === this.siteIds.MOTORPOINT) {
                    this.leasingType = LEASING_PLAN_TYPES.TYPE_FINANCIAL;
                    this.leasingPurpose = LEASING_PLAN_TYPES.PURPOSE_BUSINESS;

                    this.leasingService.setActiveLeasingToggles({
                        leasingType: this.leasingType,
                        leasingPurpose: this.leasingPurpose
                    });

                    return;
                }
                else {
                    if (this.leasingTypes?.length) {
                        this.leasingType = this.leasingTypes[0].value;
                    }
                    if (this.leasingPurposes?.length) {
                        this.leasingPurpose = this.leasingPurposes[0].value;
                    }

                    this.leasingService.setActiveLeasingToggles({
                        leasingType: this.leasingType,
                        leasingPurpose: this.leasingPurpose
                    });
                }
            }
        });

        // Applying initial values to form
        this.form = this.fb.group(
            {
                downPayment: [this.downPayment],
                monthlyTerms: [this.monthlyTerms],
                yearlyDistanceInKm: [this.yearlyDistanceInKm],
                residualValue: [this.residualValue],
                leasingPlanType: [this.leasingType],
                leasingPurpose: [this.leasingPurpose],
            },
            {
                updateOn: 'change',
            }
        );

        this.setInitialValues();

        this.dataReady = true;
        this.cdr.markForCheck();
    }

    ngOnChanges(changes: any): void {
        this.isDisabled = changes?.isDisabled?.currentValue ?? false;

        if (this.form && changes?.downPayment?.currentValue) {
            this.currentDownPaymentTerms = changes.downPayment.currentValue;
            this.currentDownPaymentTermsLabel = Number(this.currentDownPaymentTerms);
            this.form.get('downPayment')?.setValue(this.currentDownPaymentTerms);
        }

        this.cdr.detectChanges();
    }

    setInitialValues() {
        if (this.form) {
            if (this.showResidualValue) {
                this.currentResidualValueLabel = Number(this.residualValue);
            }

            this.currentMileageTerms = this.form?.get('yearlyDistanceInKm')?.value;
            this.currentMileageTermsLabel = Number(this.currentMileageTerms);

            this.currentLeasingMonthTerms = this.leasingMonthsRange?.find((m) => m.isDefault);
            this.currentLeasingMonthTermsLabel = Number(this.currentLeasingMonthTerms);
            this.currentMontshButtonsValue = this.currentLeasingMonthTerms as IToggleButtonsGroupItem;

            this.currentDownPaymentTerms = this.form.get('downPayment')?.value;
            this.currentDownPaymentTermsLabel = Number(this.currentDownPaymentTerms);

            this.currentLeasingTypeValue = this.form.get('leasingPlanType')?.value;
            this.currentLeasingTypeLabel = this.currentLeasingTypeValue;

            this.currentLeasingPurposeValue = this.form.get('leasingPurpose')?.value;
            this.currentLeasingPurposeLabel = this.currentLeasingPurposeValue;
        }

        // Map month range to toggle buttons range
        if (this.leasingMonthsRange) {
            this.montshButtonsRange = this.leasingMonthsRange.map((item: ITickmark) => {
                return item as IToggleButtonsGroupItem;
            });
        }
    }

    // Input handlers
    handleFirstPaymentTermChange(val?: ITickmark) {
        if (this.compareValuesHandler(this.currentDownPaymentTerms, val)) {
            return;
        }

        this.currentDownPaymentTerms = val;
        this.onSubmit();
    }

    handleFirstPaymentTermLabelChange(val?: number) {
        this.currentDownPaymentTermsLabel = val;
    }

    selectLeasingPlan(val: IRadioGroupItem) {
        this.currentLeasingTypeValue = val.value;
        this.currentLeasingTypeLabel = val.label;

        this.leasingService.setActiveLeasingToggles({
            leasingType: val.value,
            leasingPurpose: this.currentLeasingPurposeValue
        });

        this.onSubmit();
    }

    selectLeasingPurpose(val: IRadioGroupItem) {
        this.currentLeasingPurposeValue = val.value;
        this.currentLeasingPurposeLabel = val.label;

        this.leasingService.setActiveLeasingToggles({
            leasingType: this.currentLeasingTypeValue,
            leasingPurpose: val.value
        });

        this.submitIfChanged();
    }

    handleMonthTermChange(val?: ITickmark | IToggleButtonsGroupItem) {
        if (this.compareValuesHandler(this.currentLeasingMonthTerms, val)) {
            return;
        }

        // Set correct default value
        this.leasingMonthsRange = this.leasingMonthsRange?.map((item: ITickmark) => {
            return {
                ...item,
                isDefault: item.value === val?.value
            }
        });


        this.currentLeasingMonthTerms = this.leasingMonthsRange?.find(x => x.isDefault);
        this.currentLeasingMonthTermsLabel = Number(this.currentLeasingMonthTerms?.value);

        // Map to Toggle Buttons
        this.montshButtonsRange = this.leasingMonthsRange as IToggleButtonsGroupItem[];
        this.currentMontshButtonsValue = this.currentLeasingMonthTerms as IToggleButtonsGroupItem;

        this.submitIfChanged();
    }

    handleMileageChange(val?: ITickmark) {
        if (this.compareValuesHandler(this.currentMileageTerms, val)) {
            return;
        }

        this.currentMileageTerms = val;
        this.submitIfChanged();
    }

    handleMileageLabelChange(val?: number) {
        this.currentMileageTermsLabel = val;
    }

    handleResidualValueChange(val?: ITickmark) {
        if (this.compareValuesHandler(this.currentResidualValue, val)) {
            return;
        }

        this.currentResidualValue = val;
        this.submitIfChanged();
    }

    handleResidualValueLabelChange(val?: number) {
        this.currentResidualValueLabel = val;
    }

    private submitIfChanged() {
        const currentFormData = {
            downPayment: Number(this.currentDownPaymentTerms?.value ?? this.downPayment),
            monthlyTerms: Number(this.currentLeasingMonthTerms?.value ?? this.monthlyTerms),
            yearlyDistanceInKm: Number(this.currentMileageTerms?.value ?? this.yearlyDistanceInKm),
            residualValue: Number(this.currentResidualValue?.value ?? this.residualValue),
            leasingPlanType: (this.currentLeasingTypeValue ?? this.leasingType),
            leasingPurpose: (this.currentLeasingPurposeValue ?? this.leasingPurpose),
        };

        if (this.hasEmptyFields(currentFormData)) {
            console.warn('Cannot submit: Some fields are empty.');
            return;
        }

        if (!this.hasLoanDataChanged(currentFormData)) {
            return;
        }

        this.previousFormData = { ...currentFormData };  // Update previousLoanData to the current state
        this.onSubmit();
    }

    private hasEmptyFields(data: any): boolean {
        return Object.values(data).some(value =>
            value === null || value === undefined || value === ''
        );
    }

    private hasLoanDataChanged(newLoanData: any): boolean {
        return JSON.stringify(this.previousFormData) !== JSON.stringify(newLoanData);
    }

    onSubmit() {
        const formResult: ILeasingConfiguration = {
            downPayment: Number(this.currentDownPaymentTerms?.value ?? this.downPayment),
            monthlyTerms: Number(this.currentLeasingMonthTerms?.value ?? this.monthlyTerms),
            yearlyDistanceInKm: Number(this.currentMileageTerms?.value ?? this.yearlyDistanceInKm),
            residualValue: Number(this.currentResidualValue?.value ?? this.residualValue),
            leasingPlanType: (this.currentLeasingTypeValue ?? this.leasingType),
            leasingPurpose: (this.currentLeasingPurposeValue ?? this.leasingPurpose),
        };

        this.cdr.markForCheck();

        this.handleChange?.emit(formResult);
    }

    compareValuesHandler(currentVal: ITickmark | undefined, val: ITickmark | undefined): boolean {
        return currentVal?.value === val?.value;
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
